import { useEffect, useRef, useState } from 'react';
import { useAuthStore } from '../auth/authStore.js';
import { useThemeStore, type ThemePreference } from '../theme/theme.js';
import { AccountDialog } from './AccountDialog.js';
import { Avatar } from './Avatar.js';
import { Icon } from './Icon.js';
import { useInstallStore } from '../pwa/serviceWorker.js';
import { NotificationToggle } from './NotificationToggle.js';

/**
 * The signed-in identity in the header, with sign-out behind it.
 *
 * Sign-out used to be a bare button in the header. Behind a menu it is harder to
 * hit by accident, and the menu is where the account's own email belongs - this
 * is the one place in the app a user sees their own address.
 */
export function AccountMenu({ onSignOut }: { onSignOut: () => void }) {
  const user = useAuthStore((state) => state.user);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const theme = useThemeStore((state) => state.preference);
  const setTheme = useThemeStore((state) => state.setPreference);
  const installPrompt = useInstallStore((state) => state.prompt);
  const install = useInstallStore((state) => state.install);
  const rootRef = useRef<HTMLDivElement>(null);

  // Dismiss on an outside click or Esc. Both are registered only while the menu
  // is open, so a closed menu costs no document-level listeners.
  useEffect(() => {
    if (!open) {
      return;
    }

    function onPointerDown(event: MouseEvent): void {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        setOpen(false);
      }
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);

    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  if (!user) {
    return null;
  }

  return (
    <div className="account" ref={rootRef}>
      <button
        type="button"
        className="account-trigger"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((current) => !current)}
      >
        <Avatar name={user.displayName} id={user.id} size="sm" />
        <span className="account-name truncate">{user.displayName}</span>
        <Icon name="chevronDown" size={14} />
      </button>

      {open ? (
        <div className="account-menu" role="menu">
          <div className="account-menu-head">
            <Avatar name={user.displayName} id={user.id} />
            <span className="account-menu-identity">
              <strong className="truncate">{user.displayName}</strong>
              <span className="truncate">{user.email}</span>
            </span>
          </div>

          <hr />

          <button
            type="button"
            className="menu-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setEditing(true);
            }}
          >
            <Icon name="user" size={15} />
            Account settings
          </button>

          <div className="theme-switch" role="radiogroup" aria-label="Theme">
            {THEMES.map((option) => (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={theme === option.value}
                className={
                  theme === option.value ? 'theme-option theme-option--active' : 'theme-option'
                }
                onClick={() => setTheme(option.value)}
              >
                <Icon name={option.icon} size={14} />
                {option.label}
              </button>
            ))}
          </div>

          <hr />

          <NotificationToggle />

          {installPrompt ? (
            <button
              type="button"
              className="menu-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                void install();
              }}
            >
              <Icon name="download" size={15} />
              Install app
            </button>
          ) : null}

          <hr />

          <button type="button" className="menu-item" role="menuitem" onClick={onSignOut}>
            <Icon name="logOut" size={15} />
            Sign out
          </button>
        </div>
      ) : null}

      {editing ? <AccountDialog onClose={() => setEditing(false)} /> : null}
    </div>
  );
}

const THEMES: { value: ThemePreference; label: string; icon: 'monitor' | 'sun' | 'moon' }[] = [
  { value: 'system', label: 'System', icon: 'monitor' },
  { value: 'light', label: 'Light', icon: 'sun' },
  { value: 'dark', label: 'Dark', icon: 'moon' },
];
